/**
 * useOrbitFiles
 * Manages the current user's uploaded files list (fetch, upload, delete).
 */
import { useState, useEffect, useCallback } from 'react';
import OrbitFilesService from '../services/OrbitFilesService';
import type { OrbitChatUpload } from '../services/OrbitChatService';

export type OrbitFileContextStatus = 'unknown' | 'available' | 'unsupported' | 'missing';

export interface OrbitFileUploadPolicy {
  tier: string;
  tier_key: string;
  max_file_size_bytes: number;
  max_file_size_label: string;
  max_files_per_month: number;
  files_uploaded_this_month: number;
  files_remaining_this_month: number;
}

export interface OrbitFile {
  id: number;
  original_filename: string;
  file_size: number;
  file_size_label: string;
  mime_type: string | null;
  status: string;
  gcs_enabled: boolean;
  created_at: string;
  conversation_id: string | null;
  context_status?: OrbitFileContextStatus;
  context_status_message?: string | null;
}
interface UseOrbitFilesResult {
  files:        OrbitFile[];
  loading:      boolean;
  uploading:    boolean;
  uploadProgress: number;
  uploadPolicy: OrbitFileUploadPolicy | null;
  uploadFile:   (file: File) => Promise<void>;
  deleteFile:   (id: number) => Promise<void>;
  downloadFile: (id: number, filename: string) => Promise<void>;
  getContextUpload: (id: number) => Promise<OrbitChatUpload>;
  refresh:      () => void;
}
export function useOrbitFiles(clientId?: number, conversationId?: string, filterByConversation = false): UseOrbitFilesResult {
  const [files,           setFiles]           = useState<OrbitFile[]>([]);
  const [loading,         setLoading]         = useState(true);
  const [uploading,       setUploading]       = useState(false);
  const [uploadProgress,  setUploadProgress]  = useState(0);
  const [uploadPolicy,    setUploadPolicy]    = useState<OrbitFileUploadPolicy | null>(null);
  const [tick,            setTick]            = useState(0);
  const refresh = useCallback(() => setTick(t => t + 1), []);

  const mergeContextAvailability = useCallback((targetFiles: OrbitFile[], availabilityData: Record<string, any> | undefined) => {
    if (!availabilityData) return targetFiles;
    return targetFiles.map((file) => {
      const availability = availabilityData[String(file.id)];
      if (!availability) return file;
      return {
        ...file,
        context_status: availability.context_status,
        context_status_message: availability.message ?? null,
      };
    });
  }, []);

  const loadContextAvailability = useCallback(async (targetFiles: OrbitFile[]) => {
    const ids = targetFiles.map((file) => file.id).filter((id) => Number.isFinite(id));
    if (ids.length === 0) return;
    try {
      const res = await OrbitFilesService.getContextAvailability(ids);
      setFiles((prev) => mergeContextAvailability(prev, res?.data));
    } catch {
      // Keep the existing optimistic list state if the background availability call fails.
    }
  }, [mergeContextAvailability]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const params: Record<string, any> = {};
    if (clientId && clientId > 0)  params.client_id = clientId;
    if (filterByConversation && conversationId) params.conversation_id = conversationId;
    OrbitFilesService.list(params)
      .then((res: any) => {
        if (cancelled) return;
        const loadedFiles = (res?.data ?? []).map((file: OrbitFile) => ({
          ...file,
          context_status: 'unknown' as OrbitFileContextStatus,
          context_status_message: null,
        }));
        setFiles(loadedFiles);
        setUploadPolicy(res?.meta?.upload_policy ?? null);
        void loadContextAvailability(loadedFiles);
      })
      .catch(() => {
        if (!cancelled) {
          setFiles([]);
          setUploadPolicy(null);
        }
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [clientId, conversationId, filterByConversation, tick, loadContextAvailability]);

  const uploadFile = useCallback(async (file: File) => {
    if (uploadPolicy) {
      if (uploadPolicy.files_remaining_this_month <= 0) {
        throw new Error(`${uploadPolicy.tier} tier upload limit reached: ${uploadPolicy.max_files_per_month} files per month.`);
      }
      if (file.size > uploadPolicy.max_file_size_bytes) {
        throw new Error(`${uploadPolicy.tier} tier files are limited to ${uploadPolicy.max_file_size_label} each. Please choose a smaller file.`);
      }
    }

    setUploading(true);
    setUploadProgress(0);
    try {
      const res = await OrbitFilesService.upload(file, {
        clientId:       clientId && clientId > 0 ? clientId : undefined,
        conversationId: conversationId,
      });
      if (res?.data) {
        const nextFile: OrbitFile = {
          ...res.data,
          context_status: 'unknown',
          context_status_message: null,
        };
        setFiles(prev => [nextFile, ...prev]);
        setUploadPolicy(res?.meta?.upload_policy ?? uploadPolicy);
        void loadContextAvailability([nextFile]);
      }
    } catch (error: any) {
      const message = error?.response?.data?.message || error?.message || 'Failed to upload file';
      throw new Error(message);
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  }, [clientId, conversationId, loadContextAvailability, uploadPolicy]);

  const deleteFile = useCallback(async (id: number) => {
    await OrbitFilesService.delete(id);
    setFiles(prev => prev.filter(f => f.id !== id));
  }, []);

  const downloadFile = useCallback(async (id: number, filename: string) => {
    const res = await OrbitFilesService.getDownloadUrl(id);
    if (res?.status === 'pending') {
      alert(res.message);
      return;
    }
    if (res?.url) {
      const a = document.createElement('a');
      a.href = res.url;
      a.download = filename;
      a.rel = 'noopener';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    }
  }, []);

  const getContextUpload = useCallback(async (id: number): Promise<OrbitChatUpload> => {
    try {
      const res = await OrbitFilesService.getContextUpload(id);
      if (res?.status !== 'success' || !res?.data?.text) {
        throw new Error(res?.message || 'Unable to add file to chat context');
      }
      return res.data as OrbitChatUpload;
    } catch (error: any) {
      const message = error?.response?.data?.message || error?.message || 'Unable to add file to chat context';
      throw new Error(message);
    }
  }, []);

  return { files, loading, uploading, uploadProgress, uploadPolicy, uploadFile, deleteFile, downloadFile, getContextUpload, refresh };
}
